import {
  SearchQueryFactory,
  type SearchFactory,
  type SearchParameterName,
  type SearchParameters,
} from 'app/domain';
import { URL_ONLY_SIGNALS } from './explicitSignals';

const queryFactory = new SearchQueryFactory();

/**
 * The domain's search query, plus the values only a url has to state — see
 * explicitSignals.ts.
 *
 * For the visitor's own search. A query for some other search (a keyword link,
 * say) should use SearchQueryFactory directly, where an unstated parameter is
 * the right default.
 *
 * What a signal asks is whether this search offers a parameter, so the answer
 * comes from the search the parameters make. The query itself is still read off
 * the parameters as given: the search clears what it cannot carry, and a query
 * that followed it would drop a parameter the caller stated.
 */
export const searchQueryAdapter = (
  searchParameters: SearchParameters,
  searchFactory: SearchFactory,
  previousSearchParameters: SearchParameters | undefined = undefined
): Record<string, string> => {
  // Made only when a signal asks, as most queries ask nothing.
  const isOffered = (name: SearchParameterName): boolean =>
    searchFactory.restore(searchParameters).parameter(name).isOffered;

  return URL_ONLY_SIGNALS.reduce(
    (query, signal) =>
      signal.isStated(searchParameters, isOffered, previousSearchParameters)
        ? { ...query, [signal.name]: signal.statedValue }
        : query,
    queryFactory.fromSearchParameters(searchParameters)
  );
};
